import { formatWithCommas } from '@/utils/format'
import { ArcsTokenIcon } from '@components/ui/ArcsTokenIcon'
import type React from 'react'
import { useNavigate } from 'react-router-dom'
import ethereumTokenIcon from '../../../public/icons/EthereumTokenIcon.svg'
import infoSvg from '../../assets/figma/118-4028/ec4f3e2f76c3b4488631774a4c48ae72036ea8df.svg'
import type { TokenType } from './TokenSelectSheet'

export type TransferModalStep = 'confirm' | 'processing' | 'success'

export type TransferStatus =
  | 'idle'
  | 'confirming'
  | 'awaiting_signature'
  | 'sending'
  | 'completed'
  | 'error'

interface TransferModalProps {
  isOpen: boolean
  step: TransferModalStep
  tokenType: TokenType
  amount: string
  recipientAddress: string
  status: TransferStatus
  error: string | null
  onConfirm: () => void
  onClose: () => void
  feeText?: string
  feeSymbol?: string
  sendToken?: TokenType
  feeToken?: TokenType
}

const CloseButton: React.FC<{ onClick: () => void; variant?: 'dark' | 'light' }> = ({
  onClick,
  variant = 'dark',
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`absolute top-4 right-4 size-6 flex items-center justify-center transition-colors ${
      variant === 'light' ? 'text-white/60 hover:text-white' : 'text-zinc-400 hover:text-zinc-600'
    }`}
    aria-label="閉じる"
  >
    <svg className="size-5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M15 9L9 15M9 9L15 15"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  </button>
)

const LoadingSpinner: React.FC = () => (
  <div className="size-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
)

const WalletIcon: React.FC = () => (
  <div className="size-12 bg-blue-100 rounded-full flex items-center justify-center">
    <svg
      className="size-6 text-blue-600"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
      />
    </svg>
  </div>
)

const ErrorIcon: React.FC = () => (
  <div className="size-12 bg-red-100 rounded-full flex items-center justify-center">
    <svg
      className="size-6 text-red-600"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  </div>
)

const PROCESSING_STATUS_CONFIG: Record<
  Exclude<TransferStatus, 'idle' | 'completed'>,
  { title: string; description: string; icon: 'loading' | 'wallet' | 'error' }
> = {
  confirming: {
    title: '送金を準備中',
    description: 'トランザクションを準備しています...',
    icon: 'loading',
  },
  awaiting_signature: {
    title: 'ウォレット承認待ち',
    description: 'ウォレットでトランザクションを承認してください',
    icon: 'wallet',
  },
  sending: {
    title: '送金中',
    description: 'トランザクションを処理しています...',
    icon: 'loading',
  },
  error: {
    title: 'エラーが発生しました',
    description: '',
    icon: 'error',
  },
}

export const TransferModal: React.FC<TransferModalProps> = ({
  isOpen,
  step,
  tokenType,
  amount,
  recipientAddress,
  status,
  error,
  onConfirm,
  onClose,
  feeText,
  feeSymbol,
  sendToken,
  feeToken,
}) => {
  const navigate = useNavigate()

  if (!isOpen) return null

  const handleViewHistory = () => {
    onClose()
    navigate('/history')
  }

  const canClose = step === 'confirm' || step === 'success' || status === 'error'

  const tokenSymbol = tokenType === 'arcs' ? 'ARX' : 'ETH'
  const tokenName = tokenType === 'arcs' ? 'ARCS' : 'ETH'

  const renderProcessingIcon = () => {
    if (status === 'idle' || status === 'completed') return null
    const config = PROCESSING_STATUS_CONFIG[status]
    switch (config.icon) {
      case 'loading':
        return <LoadingSpinner />
      case 'wallet':
        return <WalletIcon />
      case 'error':
        return <ErrorIcon />
      default:
        return <LoadingSpinner />
    }
  }

  // 確認ステップ
  const renderConfirmStep = () => (
    <div className="relative w-full max-w-[402px] mx-4 mb-6 bg-white rounded-[32px] p-6 shadow-xl">
      <CloseButton onClick={onClose} />
      <div className="flex flex-col items-center gap-6 pt-2">
        <div className="relative size-10 overflow-clip">
          <img
            src={infoSvg}
            alt=""
            aria-hidden="true"
            className="absolute inset-[1.32%] block size-full max-w-none"
          />
        </div>
        <div className="flex flex-col items-center gap-2 text-center">
          <p className="text-base font-semibold text-[#18181b]">
            {formatWithCommas(amount)}
            {tokenSymbol}を
            <span className="block text-xs font-medium text-zinc-600 break-all px-2 mt-1">
              {recipientAddress}
            </span>
            へ送金しますが よろしいですか？
          </p>
          <p className="text-sm font-medium text-[#71717a]">
            こちらの処理は取り消すことができません。
          </p>
        </div>
        {feeText && feeSymbol && (
          <div className="w-full flex flex-col gap-2 p-3 bg-zinc-50 rounded-xl">
            <div className="flex items-center justify-between">
              <span className="text-xs text-zinc-500">送金額</span>
              <span className="text-sm font-medium text-zinc-900">
                {formatWithCommas(amount)} {tokenSymbol}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-zinc-500">推定手数料</span>
              <span className="text-sm font-medium text-zinc-900">
                {feeText} {feeSymbol}
              </span>
            </div>
            {sendToken === feeToken && (
              <>
                <div className="border-t border-zinc-200" />
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-zinc-700">合計</span>
                  <span className="text-sm font-semibold text-zinc-900">
                    {formatWithCommas(
                      String(
                        Number.parseFloat(amount) + Number.parseFloat(feeText.replace(/,/g, ''))
                      )
                    )}{' '}
                    {tokenSymbol}
                  </span>
                </div>
              </>
            )}
          </div>
        )}
        <button
          type="button"
          onClick={onConfirm}
          className="w-full h-12 bg-[#3b82f6] border border-[#2563eb] text-white text-base font-semibold rounded-xl shadow-[0px_0px_0px_1px_#d6f2fe,0px_0px_0px_4px_#f3fbff,0px_1px_1px_0.5px_rgba(24,24,27,0.05)] hover:bg-[#2563eb] transition-colors"
        >
          送金
        </button>
      </div>
    </div>
  )

  // 処理中ステップ
  const renderProcessingStep = () => {
    const config =
      status !== 'idle' && status !== 'completed'
        ? PROCESSING_STATUS_CONFIG[status]
        : PROCESSING_STATUS_CONFIG.confirming

    return (
      <div className="relative w-full max-w-[402px] mx-4 mb-6 bg-white rounded-[32px] p-6 shadow-xl">
        {status === 'error' && <CloseButton onClick={onClose} />}
        <div className="flex flex-col items-center gap-6 pt-2">
          {renderProcessingIcon()}
          <div className="flex flex-col items-center gap-1 text-center">
            <p className="text-base font-semibold text-[#18181b]">{config.title}</p>
            <p className="text-sm font-medium text-[#71717a]">{error || config.description}</p>
          </div>
          {status === 'error' && (
            <button
              type="button"
              onClick={onClose}
              className="w-full h-12 bg-[#71717a] text-white text-base font-semibold rounded-xl hover:bg-[#52525b] transition-colors"
            >
              閉じる
            </button>
          )}
        </div>
      </div>
    )
  }

  // 成功ステップ
  const renderSuccessStep = () => (
    <div className="relative w-full max-w-[402px] mx-4 mb-6 bg-[#2563eb] rounded-[32px] px-6 py-6 shadow-xl overflow-hidden">
      <CloseButton onClick={onClose} variant="light" />
      <div className="flex flex-col items-center gap-12 pt-2">
        <div className="size-16 bg-white rounded-full flex items-center justify-center shadow-lg">
          {tokenType === 'arcs' ? (
            <ArcsTokenIcon className="size-10" />
          ) : (
            <img src={ethereumTokenIcon} alt="" aria-hidden="true" className="size-10" />
          )}
        </div>
        <div className="flex flex-col items-center gap-4 text-center">
          <p className="text-base font-semibold text-white">{tokenName}を送金しました！</p>
          <p className="text-[40px] font-semibold text-white leading-none">
            {formatWithCommas(amount)}
            {tokenSymbol}
          </p>
          <p className="text-sm font-medium text-[#d4d4d8] leading-relaxed">
            時間を置いて履歴画面より確認いただけます。
          </p>
        </div>
        <button
          type="button"
          onClick={handleViewHistory}
          className="w-full py-3 px-5 bg-white text-[#18181b] text-base font-semibold rounded-xl hover:bg-zinc-100 transition-colors"
        >
          履歴を確認する
        </button>
      </div>
    </div>
  )

  const renderContent = () => {
    switch (step) {
      case 'confirm':
        return renderConfirmStep()
      case 'processing':
        return renderProcessingStep()
      case 'success':
        return renderSuccessStep()
      default:
        return null
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center">
      {/* 背景 */}
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        onClick={canClose ? onClose : undefined}
        aria-label={canClose ? '閉じる' : undefined}
        disabled={!canClose}
      />
      {renderContent()}
    </div>
  )
}

export default TransferModal
